/**
 * Save migrations (spec §10, §11): `migrations[n]` turns a version-`n` record into a version-`n + 1` record.
 * `migrate(raw)` reads `version`, applies the chain up to `SAVE_VERSION` on a copy (the stored original is never
 * touched), then validates with the strict schema. A version newer than this build is refused as `future`.
 *
 * v1 is the first shipped format. v2 (M3 R22k) adds the Reach block: `migrations[1]` gives a v1 record a fresh Reach
 * state for its galaxy seed (`./reach.ts`). v3 (5(e) MC-e5b) is the ware re-stride (`migrateV2ToV3`). Tests also
 * inject a synthetic v0 chain.
 */
import type { z } from 'zod'
import { fail, ok, type CmdResult } from '../career/result'
import { restrideWareRow, V6_REMAP, type WareRemap } from '../strategic/wares'
import { migrateV1ToV2 } from './reach'
import { SAVE_VERSION, saveRecordSchema, zodIssueMessage, type SaveRecord } from './record'

export type Migration = (raw: unknown) => unknown

/** The M2 ware v3 adds (T32): every M2 ware record gains it at 0 (frozen with the step, not read from the catalogue). */
const V3_ADDED_M2_WARE = 'refractory_ore'
/** The price log's "no data" value (`trade-intel/priceLog.ts` `NO_DATA`). */
const PRICE_NO_DATA = -1
/** The four per-ware rows of a price log entry (`career/schema.ts` `priceLogEntrySchema`). */
const PRICE_ROWS = ['b', 's', 'bAt', 'sAt'] as const

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/**
 * Gives every M2 ware record under `node` the added ware at 0, in place. A ware record is the `wares` of an object
 * that also holds a `components` array: the hold (`career.cargo`), cargo contents (death pods, cargo pods, world
 * wrecks) and berths (`career.storage`). A record that already names the ware keeps its value.
 */
function fillWareRecords(node: unknown): void {
  if (Array.isArray(node)) {
    for (const v of node) fillWareRecords(v)
    return
  }
  if (!isObject(node)) return
  const wares = node.wares
  if (isObject(wares) && Array.isArray(node.components) && !(V3_ADDED_M2_WARE in wares)) wares[V3_ADDED_M2_WARE] = 0
  for (const v of Object.values(node)) fillWareRecords(v)
}

/** One price row in the old layout re-strided to the new one (−1 at the added wares); any other row is left to the schema. */
function restridePriceRow(row: unknown, remap: WareRemap): unknown {
  if (!Array.isArray(row) || row.length !== remap.oldCount || !row.every((v) => typeof v === 'number')) return row
  const out = new Array<number>(remap.newCount)
  restrideWareRow(row as number[], 0, out, 0, PRICE_NO_DATA, remap)
  return out
}

/**
 * Save format v2 → v3 (5(e) MC-e5b, T32; `docs/plans/2026-10-08-5e-v7-migration-calls.md` §3):
 * - `career.priceLog`: all four rows (`b`, `s`, `bAt`, `sAt`) of every system move from the 17-ware to the
 *   19-ware strategic layout by ware id (MC-e1's `V6_REMAP`); the new ore (6) and alloy (18) read −1, no data.
 * - Every M2 ware record (hold, pods, wrecks, berths) gains `refractory_ore: 0` (`fillWareRecords`).
 * - Everything else is copied as saved: open fab orders keep the bill they were started with (`required`,
 *   `delivered`, `carry`: a platinum carry stays platinum), and M9's keys (`hulks`, kits, hangar `flaws` and
 *   `valueMult`) hold no ware index (addendum C18).
 * Works on its own copy, so the input is never touched.
 */
export function migrateV2ToV3(raw: unknown, remap: WareRemap = V6_REMAP): unknown {
  if (!isObject(raw)) throw new Error('not a save record')
  const record = structuredClone(raw)
  const career = record.career
  if (isObject(career)) {
    fillWareRecords(career)
    const log = career.priceLog
    if (isObject(log)) {
      for (const entry of Object.values(log)) {
        if (!isObject(entry)) continue
        for (const key of PRICE_ROWS) if (key in entry) entry[key] = restridePriceRow(entry[key], remap)
      }
    }
  }
  fillWareRecords(record.worlds)
  return { ...record, version: 3 }
}

export const migrations: Readonly<Record<number, Migration>> = { 1: migrateV1ToV2, 2: (raw) => migrateV2ToV3(raw) }

export interface MigrateOptions {
  /** Default: `migrations`. */
  chain?: Readonly<Record<number, Migration>>
  /** Default: `SAVE_VERSION`. */
  target?: number
  /** Default: `saveRecordSchema`. */
  schema?: z.ZodType<SaveRecord>
}

export interface Migrated {
  record: SaveRecord
  /** The version the raw record had. */
  from: number
}

/** The `version` of a raw record, or null when it has none (not an object, or not a non-negative integer). */
export function readVersion(raw: unknown): number | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const version: unknown = (raw as Record<string, unknown>).version
  return typeof version === 'number' && Number.isInteger(version) && version >= 0 ? version : null
}

/**
 * Migrates and validates a parsed record. Errors: `corrupt` (no version, or a current-version record that fails
 * validation: the message is the Zod path), `future` (saved by a newer build), `migrationFailed` (a step is
 * missing or throws, or the migrated record fails validation; `detail.version` is the stored version).
 */
export function migrate(raw: unknown, options: MigrateOptions = {}): CmdResult<Migrated> {
  const chain = options.chain ?? migrations
  const target = options.target ?? SAVE_VERSION
  const schema = options.schema ?? saveRecordSchema
  const from = readVersion(raw)
  if (from === null) return fail('corrupt', 'Not a save record: missing or invalid "version".')
  if (from > target) {
    return fail('future', `Saved by a newer version of the game (save format v${from}; this build reads up to v${target}).`, {
      version: from,
    })
  }
  let current: unknown = from === target ? raw : structuredClone(raw)
  for (let v = from; v < target; v++) {
    const step = chain[v]
    if (step === undefined) {
      return fail('migrationFailed', `Cannot migrate save format v${from}: no migration from v${v}.`, { version: from, step: v })
    }
    try {
      current = step(current)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      return fail('migrationFailed', `Migration of save format v${from} failed at v${v}: ${message}`, { version: from, step: v })
    }
    if (readVersion(current) !== v + 1) {
      return fail('migrationFailed', `Migration of save format v${from} failed at v${v}: the result is not v${v + 1}.`, {
        version: from,
        step: v,
      })
    }
  }
  const parsed = schema.safeParse(current)
  if (!parsed.success) {
    const where = zodIssueMessage(parsed.error)
    return from === target
      ? fail('corrupt', `Invalid save: ${where}`)
      : fail('migrationFailed', `Save format v${from} did not validate after migration: ${where}`, { version: from })
  }
  return ok({ record: parsed.data, from })
}
